import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { Calendar_EventTagType, type Colour } from '@tyro/api';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  RHFColorPicker,
  RHFTextField,
  useFormValidator,
} from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { ReturnTypeFromUseCalendarEventTags } from '../../api/tags/list';
import { useUpsertCalendarEventTag } from '../../api/tags/upsert';

type CalendarTagFormState = {
  name: string;
  type: Calendar_EventTagType;
  description?: string | null;
  id?: number;
  icon?: string | null;
  colour?: Colour | null;
};

export type UpsertCalendarEventTagModalProps = {
  open: boolean;
  onClose: () => void;
  initialState: Partial<ReturnTypeFromUseCalendarEventTags> | null;
};

export function UpsertCalendarEventTagModal({
  open,
  onClose,
  initialState,
}: UpsertCalendarEventTagModalProps) {
  const { t } = useTranslation(['common', 'calendar']);

  const { mutateAsync: saveCustomTag, isPending } = useUpsertCalendarEventTag();
  const { resolver, rules } = useFormValidator<CalendarTagFormState>();
  const { control, handleSubmit, reset } = useForm<CalendarTagFormState>({
    resolver: resolver({
      name: rules.required(),
      type: rules.required(),
    }),
  });

  const onSubmit = handleSubmit(({ name, description, type, ...formData }) =>
    saveCustomTag(
      {
        id: initialState?.id,
        name: [{ locale: 'en', value: name }],
        description: [{ locale: 'en', value: description }],
        ...formData,
        groupTagType: Calendar_EventTagType.PublicCalendar, // Default to this until we have more types
      },
      {
        onSuccess: onClose,
      },
    ),
  );

  useEffect(() => {
    if (open) {
      reset({ ...initialState });
    }
  }, [initialState]);

  return (
    <Dialog
      open={open}
      scroll="paper"
      fullWidth
      maxWidth="xs"
      onClose={onClose}
    >
      <form onSubmit={onSubmit}>
        <DialogTitle>
          {t(
            `calendar:${initialState?.id ? 'editCalendarTag' : 'createCalendarTag'}`,
          )}
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <RHFTextField
              label={t('common:name')}
              controlProps={{
                name: 'name',
                control,
              }}
            />

            <RHFTextField
              label={t('common:description')}
              controlProps={{
                name: 'description',
                control,
              }}
            />

            {/* <RHFSelect
              label={t('common:type')}
              options={[Calendar_EventTagType.PublicCalendar]}
              getOptionLabel={(option) => t(`calendar:groupTagType.${option}`)}
              controlProps={{ name: 'type', control }}
            /> */}

            <RHFColorPicker
              label={t('common:colour')}
              controlProps={{
                name: 'colour',
                control,
              }}
            />

            <RHFTextField
              label={t('common:icon')}
              controlProps={{
                name: 'icon',
                control,
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>

          <Button type="submit" variant="contained" loading={isPending}>
            {t('common:actions.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
